import { useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

/**
 * Thin spell-yellow progress bar pinned to the top of the viewport. Slides
 * in whenever the router is between pages (loading or transitioning) and
 * fades out when idle. Designed to read as a continuous magig signal even
 * on sub-150ms navigations — the full-screen PageLoader only kicks in when
 * a navigation actually stalls, this bar covers everything else.
 *
 * Sits above the sticky TopNav (z-index 50 vs the nav's 30).
 */
export function RouterProgress() {
  const isLoading = useRouterState({
    select: (s) => s.status === 'pending' || s.isLoading || s.isTransitioning,
  });

  // Smooth the indicator so it doesn't flash on a 5ms swap. We only show
  // the bar if the loading state persists past one frame.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (isLoading) {
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    }
    setVisible(false);
    return undefined;
  }, [isLoading]);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-x-0 top-0 z-50 h-[2px] overflow-hidden"
    >
      <div
        className={`h-full origin-left transform bg-[var(--spell)] transition-[transform,opacity] ${
          visible
            ? 'translate-x-0 scale-x-100 opacity-100 duration-[1200ms] ease-out'
            : '-translate-x-full scale-x-0 opacity-0 duration-200 ease-out'
        }`}
        style={{
          boxShadow: '0 0 12px var(--spell), 0 0 4px var(--spell-soft)',
        }}
      />
    </div>
  );
}
